import {
  PeopleOutlined,
  CheckCircleOutlined,
  BlockOutlined,
  DeleteOutline,
  SyncAltOutlined,
  Visibility,
} from '@mui/icons-material';
import { CardContainer, MetricCard, SearchBar, Dropdown, DataTable, Tag } from 'finara-components';
import type { DropdownOption, DataTableColumn } from 'finara-components';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import { ConfirmDeleteModal } from '@/components/admin/employees/ConfirmDeleteModal';
import { ErrorState, LoadingState, EmptyState, CsvExportButton } from '@/components/common';
import { Navbar } from '@/components/super-admin/general';
import { superAdminNavItems } from '@/data/super-admin/general/navigationData';
import {
  useUsersWithoutOrganization,
  useToggleUserActive,
  useDeleteUser,
} from '@/hooks/queries/useUsers';
import type { UserWithoutOrganization } from '@/types/user';

export const UnregisteredUsersPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserWithoutOrganization | null>(null);

  const { data: users, isLoading, error } = useUsersWithoutOrganization();
  const toggleUserActive = useToggleUserActive();
  const deleteUser = useDeleteUser();

  const statusOptions: DropdownOption[] = [
    { value: '', label: 'Todos los estados' },
    { value: 'active', label: 'Activo' },
    { value: 'inactive', label: 'Inactivo' },
  ];

  const filteredUsers = useMemo(() => {
    if (!users) return [];
    return users.filter(user => {
      const matchesSearch = searchTerm
        ? user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email.toLowerCase().includes(searchTerm.toLowerCase())
        : true;

      const matchesStatus = statusFilter
        ? (user.isActive && statusFilter === 'active') ||
          (!user.isActive && statusFilter === 'inactive')
        : true;

      return matchesSearch && matchesStatus;
    });
  }, [users, searchTerm, statusFilter]);

  const totalUsers = users?.length ?? 0;
  const activeUsers = users?.filter(u => u.isActive).length ?? 0;
  const inactiveUsers = totalUsers - activeUsers;

  const handleToggleActive = async (user: UserWithoutOrganization) => {
    try {
      await toggleUserActive.mutateAsync({ userId: user.id, data: { isActive: !user.isActive } });
      toast.success(`Usuario ${user.isActive ? 'desactivado' : 'activado'} exitosamente`);
    } catch {
      toast.error('Error al cambiar el estado del usuario. Intenta nuevamente.');
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    try {
      await deleteUser.mutateAsync(selectedUser.id);
      setIsDeleteModalOpen(false);
      setSelectedUser(null);
      toast.success('Usuario eliminado exitosamente');
    } catch {
      toast.error('Error al eliminar el usuario. Intenta nuevamente.');
    }
  };

  const openDeleteModal = (user: UserWithoutOrganization) => {
    setSelectedUser(user);
    setIsDeleteModalOpen(true);
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  const getUserInitials = (user: UserWithoutOrganization) => {
    if (user.name.trim()) {
      return user.name
        .split(' ')
        .map(n => n[0])
        .join('')
        .substring(0, 2)
        .toUpperCase();
    }
    return user.email.substring(0, 2).toUpperCase();
  };

  const columns: DataTableColumn<UserWithoutOrganization>[] = [
    {
      key: 'user',
      label: 'Usuario',
      render: (_, user) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-semibold text-gray-700">{getUserInitials(user)}</span>
          </div>
          <div>
            {user.name
              ? <div className="font-medium text-brand-white">{user.name}</div>
              : <div className="text-xs text-amber-600 font-medium italic">Sin nombre registrado</div>
            }
            <div className="text-sm text-muted">{user.email}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'phone',
      label: 'Teléfono',
      render: (_, user) => (
        <span className="text-sm text-brand-white">
          {user.countryCode && user.phoneNumber ? `${user.countryCode} ${user.phoneNumber}` : '—'}
        </span>
      ),
    },
    {
      key: 'rfc',
      label: 'RFC',
      render: (_, user) => <span className="text-sm text-brand-white">{user.rfc ?? '—'}</span>,
    },
    {
      key: 'status',
      label: 'Estado',
      render: (_, user) => (
        <Tag variant={user.isActive ? 'green' : 'gray'}>
          {user.isActive ? 'Activo' : 'Inactivo'}
        </Tag>
      ),
    },
    {
      key: 'createdAt',
      label: 'Fecha de Registro',
      render: (_, user) => <span className="text-sm text-muted">{formatDate(user.createdAt)}</span>,
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (_, user) => (
        <div className="flex items-center gap-1">
          <button
            onClick={() => void navigate(`/clientes/sin-organizacion/${user.id}`)}
            className="p-2 text-muted hover:text-brand-white hover:bg-white/5 rounded transition-colors"
            title="Ver detalles"
          >
            <Visibility fontSize="small" />
          </button>
          <button
            onClick={() => void handleToggleActive(user)}
            className="p-2 text-muted hover:text-brand-white hover:bg-white/5 rounded transition-colors"
            title={user.isActive ? 'Desactivar usuario' : 'Activar usuario'}
            disabled={toggleUserActive.isPending}
          >
            <SyncAltOutlined fontSize="small" />
          </button>
          <button
            onClick={() => openDeleteModal(user)}
            className="p-2 text-muted hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
            title="Eliminar usuario"
          >
            <DeleteOutline fontSize="small" />
          </button>
        </div>
      ),
    },
  ];

  if (error) {
    return (
      <div className="min-h-screen">
        <Navbar navItems={superAdminNavItems} activeItem="Clientes" />
        <ErrorState title="Error al cargar los usuarios" showRetry />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <Navbar navItems={superAdminNavItems} activeItem="Clientes" />
        <LoadingState message="Cargando usuarios..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navbar navItems={superAdminNavItems} activeItem="Clientes" />

      <div className="pt-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-[21px] font-bold text-brand-white flex items-center gap-2">
                <PeopleOutlined />
                Usuarios sin Organización
              </h1>
              <p className="text-muted text-sm mt-1">
                {filteredUsers.length} de {totalUsers} usuarios &middot; {activeUsers} activos
                &middot; {inactiveUsers} inactivos
              </p>
            </div>
            <CsvExportButton
              data={filteredUsers}
              filename="usuarios-sin-organizacion"
              columns={user => ({
                ID: user.id,
                Nombre: user.name,
                Email: user.email,
                Teléfono:
                  user.countryCode && user.phoneNumber
                    ? `+${user.countryCode} ${user.phoneNumber}`
                    : '',
                Estado: user.isActive ? 'Activo' : 'Inactivo',
                RFC: user.rfc ?? '',
                CURP: user.curp ?? '',
                'Fecha de Registro': formatDate(user.createdAt),
              })}
              variant="white"
              size="small"
              buttonText="Exportar"
            />
          </div>
        </div>

        {/* Metric Cards */}
        <div className="grid grid-cols-1 md:!grid-cols-3 gap-4 mb-6">
          <MetricCard
            title="Total de Usuarios"
            value={totalUsers.toString()}
            icon={<PeopleOutlined className="text-blue-500" />}
            variant="white"
          />
          <MetricCard
            title="Usuarios Activos"
            value={activeUsers.toString()}
            icon={<CheckCircleOutlined className="text-green-500" />}
            variant="white"
          />
          <MetricCard
            title="Usuarios Inactivos"
            value={inactiveUsers.toString()}
            icon={<BlockOutlined className="text-red-500" />}
            variant="white"
          />
        </div>

        {/* Table Card */}
        <CardContainer>
          <div>
            {/* Filters */}
            <div className="flex flex-col lg:!flex-row lg:items-center gap-4 mb-6">
              <SearchBar
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder="Buscar por nombre o email..."
                className="w-full lg:!w-auto flex-1"
              />
              <Dropdown
                id="status-filter"
                value={statusFilter}
                onChange={value => setStatusFilter(String(value))}
                options={statusOptions}
                placeholder="Todos los estados"
                className="w-full lg:!w-auto"
              />
            </div>

            {/* Table */}
            {filteredUsers.length > 0 ? (
              <DataTable columns={columns} data={filteredUsers} />
            ) : (
              <EmptyState
                Icon={<PeopleOutlined />}
                title="No se encontraron usuarios"
                description={
                  searchTerm || statusFilter
                    ? 'Intenta ajustar los filtros de búsqueda'
                    : 'No hay usuarios sin organización registrados'
                }
                useCard={false}
                className="py-8"
              />
            )}
          </div>
        </CardContainer>
      </div>

      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedUser(null);
        }}
        onConfirm={() => void handleDeleteUser()}
        employeeName={selectedUser?.name || selectedUser?.email || ''}
        isLoading={deleteUser.isPending}
      />
    </div>
  );
};
